import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import * as Sentry from '@sentry/angular';
import {
  CashInCashRegister,
  CashOutCashRegister,
  CashTransactionLog,
  SyncCashRegisterUpdates
} from '@shared';
import { normalizeWireDates } from '@shared/helpers/normalize-wire-dates';
import { NativeServiceSyncStatus } from '@shared/models/native-service-syncable';
import { ReplicationSyncStatus } from '@shared/models/replication-sync-status';
import { OnlineOfflineService } from '../online-offine.service';
import { AppSettingsService } from '../app-settings.service';
import { CashTransactionService } from '../cash-transaction.service';
import { SyncStatusManager } from './sync-status-manager.service';
import { classifySyncFailure } from './sync-failure';

type CashTransaction = CashInCashRegister | CashOutCashRegister;

/**
 * Pushes locally-stored cash transactions to the Edara backend, sourced
 * entirely from Firestore (independent of the Desktop app).
 *
 * Each cycle reads the records still pending sync, claims them as `Syncing`
 * to guard against concurrent double-sends, posts them to the backend grouped
 * by cash register, then marks them `Synced` on success or rolls them back to
 * `UnSynced` for retry on failure. The run is non-blocking and
 * self-coalescing, so it is safe to invoke from login, reconnect, periodic
 * and post-transaction triggers.
 */
@Injectable({ providedIn: 'root' })
export class CashTransactionSyncService {
  private readonly http = inject(HttpClient);
  private readonly onlineOfflineService = inject(OnlineOfflineService);
  private readonly cashTransactionService = inject(CashTransactionService);
  private readonly syncStatusManager = inject(SyncStatusManager);

  private readonly edaraCoreBaseUrl: string;
  private readonly entityType = 'cashTransaction';
  private isSyncing = false;

  constructor() {
    this.edaraCoreBaseUrl = this.normalizeBaseUrl(
      AppSettingsService.appSettings.edaraCoreApi.baseUrl
    );
  }

  /**
   * Push every unsynced cash transaction to the backend. Non-blocking and
   * self-coalescing — safe to call from the periodic/login/online triggers.
   */
  async pushUnsyncedToServer(): Promise<void> {
    if (this.isSyncing || !this.onlineOfflineService.isOnline) return;

    this.isSyncing = true;
    try {
      const unsynced = await this.cashTransactionService.getUnsyncedAsync();
      // `Failed` records were rejected on an earlier cycle; they keep retrying
      // so a rejection the backend later accepts is never stranded.
      const failed = await this.cashTransactionService.getFailedAsync();
      const staleSyncing = await this.cashTransactionService.getStaleSyncingAsync();
      const pending = [...unsynced, ...failed, ...staleSyncing];
      if (pending.length === 0) return;

      // Claim the records so a concurrent cycle cannot double-send them.
      const claimed = await this.syncStatusManager.applyStatus(
        this.entityType,
        pending,
        ReplicationSyncStatus.Syncing
      );

      for (const [cashRegisterId, group] of this.groupByCashRegister(claimed)) {
        await this.syncGroup(cashRegisterId, group);
      }
    } catch (error) {
      console.error('[CashTransactionSync] Push failed:', error);
      Sentry.captureException(error);
    } finally {
      this.isSyncing = false;
    }
  }

  private async syncGroup(
    cashRegisterId: string,
    transactions: CashTransaction[]
  ): Promise<void> {
    const payload = new SyncCashRegisterUpdates();
    payload.id = cashRegisterId;
    payload.cashRegisterStatusLogs = [];
    payload.cashTransactionLogs = transactions.map(tx => this.toCashTransactionLog(tx));

    try {
      await firstValueFrom(
        this.http.put(
          `${this.edaraCoreBaseUrl}/api/pos/cashregisters/sync/${cashRegisterId}`,
          payload
        )
      );
      await this.syncStatusManager.applyStatus(
        this.entityType,
        transactions,
        ReplicationSyncStatus.Synced
      );
    } catch (error: any) {
      // A backend rejection becomes `Failed` (with the reason); a request that
      // never reached the backend stays `UnSynced`. Both are retried next
      // cycle — localId idempotency makes a re-send safe even if the request
      // actually landed.
      const failure = classifySyncFailure(error);
      await this.syncStatusManager.applyStatus(
        this.entityType,
        transactions,
        failure.status,
        failure.message
      );
      console.error(
        `[CashTransactionSync] Backend sync failed for cash register ${cashRegisterId}; marked ${failure.status}.`,
        error
      );
      Sentry.captureException(error);
    }
  }

  /**
   * Rebuild the backend wire shape from the Firestore record.
   *
   * Normalised on the way out because `recordedDate` is a
   * `{seconds, nanoseconds}` map on any transaction restored from the Desktop,
   * and the backend's model binder rejects the map outright.
   */
  private toCashTransactionLog(tx: CashTransaction): CashTransactionLog {
    return normalizeWireDates({
      amount: tx.amount,
      note: tx.note,
      transactionType: tx.transactionType,
      recordedBy: tx.recordedBy,
      recordedDate: tx.recordedDate,
      syncMetadata: {
        localId: tx.metadata!.localId,
        syncStatus: NativeServiceSyncStatus.Pending
      }
    });
  }

  private groupByCashRegister(
    transactions: CashTransaction[]
  ): Map<string, CashTransaction[]> {
    const groups = new Map<string, CashTransaction[]>();
    for (const tx of transactions) {
      const id = tx.cashRegisterId ?? tx.id;
      if (!id) continue;
      const bucket = groups.get(id) ?? [];
      bucket.push(tx);
      groups.set(id, bucket);
    }
    return groups;
  }

  private normalizeBaseUrl(url: string): string {
    return url?.endsWith('/') ? url.slice(0, -1) : url;
  }
}
